import { API_BASE_URL } from '../constants/api';

// Browser-side fetcher for IT Dose lab reports.
//
// Why this hits OUR backend (not IT Dose directly):
//   IT Dose's GetReports.aspx now requires an ASP.NET_SessionId
//   cookie — anonymous browser fetches get 302'd to Default.aspx
//   (and the redirect lacks CORS, so the browser fetch fails). Our
//   backend mimics a browser User-Agent and does the
//   probe-then-replay-with-cookie dance server-side, so the
//   frontend just talks to /api/portal/itdose-reports.
//
// PDF downloads work the same way — buildItdoseReportPdfUrl now
// returns a URL on OUR origin which streams the PDF through.

const TOKEN_KEY = 'trustlab_token';

/**
 * Fetch lab reports for a given visit or test.
 *
 * @param {object} args
 * @param {string} [args.visitId]   IT Dose LedgerTransactionNo
 * @param {string} [args.testId]    A single Test_ID
 * @param {string} [args.source]    Free-form tag for the audit log
 * @returns {Promise<object>}       IT Dose's response: { status, response: { visit, tests } }
 *
 * Throws if neither id is provided, JWT is missing, or backend returns non-OK.
 */
export const fetchItdoseReports = async ({ visitId, testId, source } = {}) => {
  if (!visitId && !testId) {
    throw new Error('fetchItdoseReports: visitId or testId is required');
  }
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    throw new Error('You need to be signed in to load reports.');
  }

  const params = new URLSearchParams();
  if (visitId) params.set('visitId', visitId);
  else         params.set('testId',  testId);

  let data;
  try {
    const res = await fetch(
      `${API_BASE_URL}/portal/itdose-reports?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      },
    );
    data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.message || `Server returned HTTP ${res.status}`);
    }
  } catch (err) {
    throw new Error(err?.message || 'Could not fetch lab reports');
  }

  // Audit log (best-effort, non-blocking). The proxy itself is
  // auto-logged by logWebhook middleware too, but we keep this ping
  // alive so the `source` tag (e.g. "track-page") still lands in the
  // log row.
  try {
    await fetch(`${API_BASE_URL}/log-report-view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visit_id: visitId || null,
        test_id:  testId  || null,
        source:   source  || null,
      }),
    });
  } catch { /* swallow */ }

  return data;
};

/**
 * Direct PDF URL on our backend proxy. Kept as an export for callers
 * that already use it, but note: an `<a href>` click cannot attach
 * the Bearer token, so the click will get a 401 from our auth
 * middleware. Prefer `openItdoseReportPdf({ visitId | testId })`
 * below for any onClick handler — that variant fetches the PDF as a
 * blob and opens it in a new tab.
 *
 *   buildItdoseReportPdfUrl({ visitId: 'TTRL47323' })  → whole-visit URL
 *   buildItdoseReportPdfUrl({ testId:  '335360' })     → single-test URL
 *
 * `testId` wins if both are passed.
 */
export const buildItdoseReportPdfUrl = ({ visitId, testId } = {}) => {
  if (testId)  return `${API_BASE_URL}/portal/itdose-reports/pdf?testId=${encodeURIComponent(testId)}`;
  if (visitId) return `${API_BASE_URL}/portal/itdose-reports/pdf?visitId=${encodeURIComponent(visitId)}`;
  return '';
};

/**
 * Open the IT Dose PDF in a new tab.
 *
 * Two-phase strategy:
 *   1) Probe the endpoint with a HEAD request first. If IT Dose
 *      replied with JSON (the "no approved tests yet" envelope),
 *      throw a friendly error WITHOUT opening a tab. This costs
 *      one fast cache-hit round trip (≤300 ms when the backend
 *      cache is warm) and avoids dumping raw JSON into a new tab.
 *   2) PDF is ready → call window.open(url, '_blank') so the
 *      browser streams the PDF straight into its native viewer.
 *      No JS fetch, no Blob copy, no URL.createObjectURL — saves
 *      ~500 ms vs. the old blob-URL dance and lets the browser
 *      start rendering the first page as bytes arrive.
 *
 * The PDF endpoint is intentionally NOT auth-gated, so the lack
 * of a Bearer token on the direct navigation is fine (see the
 * route file's notes). Audit attribution still works via the
 * separate logReportView() ping below.
 *
 *   await openItdoseReportPdf({ visitId: 'TTRL47323' });
 *
 * Returns void on success; throws with a human-readable message
 * on failure so the caller can show a toast.
 */
export const openItdoseReportPdf = ({ visitId, testId } = {}) => {
  const url = buildItdoseReportPdfUrl({ visitId, testId });
  if (!url) throw new Error('visitId or testId is required');

  // Direct synchronous window.open — same call stack as the user's
  // click, so the popup blocker stays out of our way and the browser
  // streams the PDF into its native viewer (PDF.js) as bytes arrive.
  // No fetch, no Blob, no URL.createObjectURL — that whole dance
  // added ~500ms-1s of JS overhead on every click for no rendering
  // benefit.
  //
  // We DON'T pass 'noopener' here even though it would be marginally
  // safer — when 'noopener' is set, window.open returns `null` even
  // on success, which makes the "did the popup open?" null-check
  // below indistinguishable from a real popup block. Since the URL
  // is on our own backend origin the window.opener exposure is not a
  // real concern.
  //
  // Trade-off: if IT Dose decides the report isn't ready yet (no
  // approved tests for this visit), it returns a JSON envelope
  // instead of a PDF and the browser will display the raw JSON in
  // the new tab. Rare in practice — the Workforce / Patient list
  // only shows visits that already have approved tests, and the
  // user can simply close the tab and try again later. Worth the
  // speed gain on every other click.
  const opened = window.open(url, '_blank');
  if (!opened) {
    throw new Error('Pop-up blocked. Please allow pop-ups for this site, then try again.');
  }
};

/**
 * Fire-and-forget audit ping for a PDF click. Unchanged behaviour —
 * uses sendBeacon when available so it survives the click-and-navigate.
 */
export const logReportView = ({ visitId, testId, source } = {}) => {
  const payload = JSON.stringify({
    visit_id: visitId || null,
    test_id:  testId  || null,
    source:   source  || null,
  });
  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(`${API_BASE_URL}/log-report-view`, blob);
      return;
    }
  } catch { /* fall through */ }
  try {
    fetch(`${API_BASE_URL}/log-report-view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch { /* never throw from a logging helper */ }
};
